import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, LoadingSpinner } from '../components/Layout';
import api, { CompanyEvent } from '../services/api';

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todayAssignment, setTodayAssignment] = useState<any>(null);
  const [weeklyHours, setWeeklyHours] = useState(0);
  const [ptoBalances, setPtoBalances] = useState<any[]>([]);
  const [pointsBalance, setPointsBalance] = useState<any>(null);
  const [streak, setStreak] = useState(0);
  const [upcomingEvents, setUpcomingEvents] = useState<CompanyEvent[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const ws = getWeekStart(today);
      const weekStart = `${ws.getFullYear()}-${String(ws.getMonth() + 1).padStart(2, '0')}-${String(ws.getDate()).padStart(2, '0')}`;

      const [assignmentRes, hoursRes, ptoRes, pointsRes, streakRes, eventsRes] = await Promise.all([
        api.getTodaysAssignment().catch(() => null),
        api.getWeeklyHours(weekStart).catch(() => ({ hours: 0 })),
        api.getPTOBalances().catch(() => []),
        api.getPointsBalance().catch(() => null),
        api.getCurrentStreak().catch(() => ({ streak: 0 })),
        api.getUpcomingEvents(5).catch(() => []),
      ]);

      setTodayAssignment(assignmentRes);
      setWeeklyHours(hoursRes?.hours || 0);
      setPtoBalances(Array.isArray(ptoRes) ? ptoRes : []);
      setPointsBalance(pointsRes);
      setStreak(streakRes?.streak || 0);
      setUpcomingEvents(Array.isArray(eventsRes) ? eventsRes : []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeekStart = (date: Date) => {
    const d = new Date(date);
    const dow = d.getDay(); // 0=Sun..6=Sat
    const daysBack = (dow - 4 + 7) % 7; // Back to most recent Thursday
    d.setDate(d.getDate() - daysBack);
    return d;
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'Employee';
  const vacationBalance = ptoBalances.find(b => b.balance_type === 'vacation');

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <Card className="border-l-4 border-l-yellow-400">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-white">Welcome back, {firstName}</h1>
            {todayAssignment && (
              <p className="text-gray-400 mt-1">
                Your shift: {todayAssignment.start_time} - {todayAssignment.end_time}
              </p>
            )}
            <p className="text-gray-400 mt-1">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
          <Button variant="secondary" onClick={() => navigate('/time-clock')}>
            Time Clock
          </Button>
        </div>
      </Card>

      {/* My Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-800 border-l-4 border-l-yellow-400">
          <div className="text-center">
            <div className="text-gray-400 text-sm">Hours This Week</div>
            <div className="text-3xl font-bold text-yellow-400 mt-2">{Number(weeklyHours || 0).toFixed(1)}</div>
            <div className="text-gray-500 text-sm mt-1">/ 40</div>
          </div>
        </Card>

        <Card className="bg-gray-800 border-l-4 border-l-blue-400">
          <div className="text-center">
            <div className="text-gray-400 text-sm">PTO Balance</div>
            <div className="text-3xl font-bold text-yellow-400 mt-2">
              {Number(vacationBalance?.available_days || 0).toFixed(1)}
            </div>
            <div className="text-gray-500 text-sm mt-1">days</div>
          </div>
        </Card>

        <Card className="bg-gray-800 border-l-4 border-l-green-400">
          <div className="text-center">
            <div className="text-gray-400 text-sm">Points Balance</div>
            <div className="text-3xl font-bold text-yellow-400 mt-2">
              {pointsBalance?.current_balance || 0}
            </div>
            <div className="text-gray-500 text-sm mt-1">pts</div>
          </div>
        </Card>

        <Card className="bg-gray-800 border-l-4 border-l-purple-400">
          <div className="text-center">
            <div className="text-gray-400 text-sm">Current Streak</div>
            <div className="text-3xl font-bold text-yellow-400 mt-2">{streak}</div>
            <div className="text-gray-500 text-sm mt-1">days on-time</div>
          </div>
        </Card>
      </div>

      {/* Today's Assignment */}
      {todayAssignment && (
        <Card className="border-l-4 border-l-yellow-400">
          <h2 className="text-xl font-bold text-white mb-4">Today's Assignment</h2>
          <div className="bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-white font-medium">Workstation Assignment</div>
                <div className="text-gray-400 text-sm mt-1">
                  {todayAssignment.start_time} - {todayAssignment.end_time}
                </div>
                {todayAssignment.notes && (
                  <div className="text-gray-400 text-sm mt-2">{todayAssignment.notes}</div>
                )}
              </div>
              <Button onClick={() => navigate('/queue')}>
                Go to My Queue
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <Card className="border-l-4 border-l-blue-400">
          <h2 className="text-xl font-bold text-white mb-4">Upcoming Events</h2>
          <div className="space-y-3">
            {upcomingEvents.map(event => (
              <div key={event.id} className="bg-gray-700 p-3 rounded-lg flex items-start gap-3">
                <div
                  className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: getEventColor(event.eventType, event.color) }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium">{event.title}</div>
                  <div className="text-gray-400 text-sm">
                    {formatEventDate(event.startTime, event.allDay)}
                    {event.location && ` • ${event.location}`}
                  </div>
                  {event.description && (
                    <div className="text-gray-500 text-sm mt-1 truncate">{event.description}</div>
                  )}
                </div>
                <span className="text-xs px-2 py-1 rounded bg-gray-600 text-gray-300 capitalize flex-shrink-0">
                  {event.eventType.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Quick Links */}
      <Card className="border-l-4 border-l-yellow-400">
        <h2 className="text-xl font-bold text-white mb-4">Quick Links</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <button
            onClick={() => navigate('/schedule')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex flex-col items-center"
          >
            <div className="text-yellow-400 mb-2">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="text-white text-sm font-medium">Schedule</div>
          </button>
          <button
            onClick={() => navigate('/time-off')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex flex-col items-center"
          >
            <div className="text-yellow-400 mb-2">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-white text-sm font-medium">Time Off</div>
          </button>
          <button
            onClick={() => navigate('/training')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex flex-col items-center"
          >
            <div className="text-yellow-400 mb-2">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="text-white text-sm font-medium">Training</div>
          </button>
          <button
            onClick={() => navigate('/documents')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex flex-col items-center"
          >
            <div className="text-yellow-400 mb-2">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="text-white text-sm font-medium">Documents</div>
          </button>
          <button
            onClick={() => navigate('/rewards')}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex flex-col items-center"
          >
            <div className="text-yellow-400 mb-2">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <div className="text-white text-sm font-medium">Rewards</div>
          </button>
        </div>
      </Card>
    </div>
  );
}

// Helper functions for event display
function getEventColor(eventType: string, customColor?: string | null): string {
  if (customColor) return customColor;
  const colors: Record<string, string> = {
    holiday: '#dc2626',
    team_meeting: '#2563eb',
    training: '#7c3aed',
    production: '#ea580c',
    shipping: '#0891b2',
    announcement: '#ca8a04',
    other: '#6b7280',
  };
  return colors[eventType] || '#6b7280';
}

function formatEventDate(dateStr: string, allDay: boolean): string {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  };
  if (!allDay) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  return date.toLocaleDateString('en-US', options);
}
